import React, {FC} from 'react';
import {useId} from '../hooks/useId';
import {DropdownContext} from './utils/DropdownContext';
import type {DropdownContextProps} from './utils/DropdownContext';
import {useControlled} from '@mui/material';

export interface DropdownProps {
    /**
      * IDs are automatically generated by component for accessibility purposes.
      * Use this prop to pass a specific ID value that will be appended: `dropdown-menu-${id}`.
      */
    id?: string;
    /**
     * Controlled open state boolean;
     *
     * @default undefined;
     */
    isOpen?: boolean;
    /**
     * The content of the component.
     *
     * @default undefined;
     */
    children?: React.ReactNode;
}

export const Dropdown: FC<DropdownProps> = ({
    children, id, isOpen
}) => {
    const [open, setOpen] = useControlled({
        controlled: isOpen,
        default: false,
        name: 'Dropdown Open State'
    });

    const anchorRef = React.useRef<HTMLElement>(null);

    const dropdownMenuId = useId(id, 'dropdown-menu');

    const handleToggle = (): void => {
        setOpen(!open);
    };

    const handleClose = (event: Event | React.SyntheticEvent): void => {
        // Return without closing menu if event originates from anchorRef (Dropdown Button)
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }
        setOpen(false);
    };

    function handleListKeyDown(event: React.KeyboardEvent): void {
        if (event.key === 'Tab' || event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
        }
    }

    // return focus to the button when we transitioned from !open -> open
    const prevOpen = React.useRef(open);

    React.useEffect(() => {
        if (prevOpen.current === true && open === false) {
            if (anchorRef.current !== null) {
                anchorRef.current.focus({
                    preventScroll: true
                });
            }
        }

        prevOpen.current = open;
    }, [open]);

    const contextValues: DropdownContextProps = {
        open,
        handleToggle,
        handleClose,
        handleListKeyDown,
        dropdownMenuId,
        anchorRef
    };

    return (
        <DropdownContext.Provider value={contextValues}>
            {children}
        </DropdownContext.Provider>
    );
};
